/**
 * Companies — screen 10, ported from the validated mockup.
 *
 * The company decides which rows a person sees, which reports they may open and what
 * colour they are drawn in, so it is maintained here rather than as a dropdown repeated
 * across three screens.
 *
 * ⚠ **Two fields the mockup does not have, and has to: the two row scopes.** The DMP
 * contractor codes scope Order Activation (`contractorScope`); the Monday company labels
 * scope Cocon Requests and Uploads (`mondayScope`). An empty scope means "no rows match",
 * which on screen is indistinguishable from a report that is simply empty — so a
 * contractor needs at least one of the two, and each picker flags a value its source has
 * never seen, because a typo here is otherwise completely silent.
 *
 * ⚠ **They come from two different lists in DMP** (28 Sep 2026). The codes are the
 * homes-CONNECT contractor written on every order — only circet and constructel, ever. A
 * new company is normally a homes-PASSED partner from `homes_passed_contractor` (Walfit,
 * S30, Jacops…), whose name is never on an order but is exactly the boards' Company label.
 * The first version of this screen required a DMP code and so could not create one.
 *
 * ⚠ Nothing destructive happens without its blast radius on screen first — the same rule
 * Drawing Control follows. "Are you sure?" is not a useful question; "this stops 4 people
 * signing in and revokes 2 report grants, and keeps 1 421 rows of history" is.
 */

import { useCallback, useEffect, useState } from "react";

import {
  COMPANY_PALETTE,
  companyForPartner,
  companyImpact,
  companyScopeProblem,
  formatDayMonthYear,
  formatNumber,
  isValidCompanyCode,
  mondayLabelsForPartner,
  suggestCompanyCode,
  type AdminCompanyRow,
  type CompanyKind,
  type CompanySources,
} from "@cocon-stats/shared";
import { AlertTriangle, Building2, Pencil, PlusCircle, Power, X } from "lucide-react";
import { toast } from "sonner";

import { Dialog, PickCard } from "@/components/Dialog";
import { ApiError, apiFetch } from "@/lib/api-client";

interface Directory {
  companies: AdminCompanyRow[];
  knownDmpCodes: string[];
  unattributedRows: number;
  snapshot: { runDate: string; rowCount: number | null } | null;
}

export function CompanyManagement() {
  const [directory, setDirectory] = useState<Directory | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  const [editing, setEditing] = useState<AdminCompanyRow | "new" | null>(null);
  const [confirming, setConfirming] = useState<AdminCompanyRow | null>(null);

  const load = useCallback(async () => {
    try {
      setDirectory(await apiFetch<Directory>("/admin/companies"));
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** Returns whether it worked, so a dialog can stay open on a refusal with its form intact. */
  const run = useCallback(
    async (work: () => Promise<unknown>, done: string): Promise<boolean> => {
      setBusy(true);
      try {
        await work();
        await load();
        toast.success(done);
        return true;
      } catch (err) {
        // The server's own sentence — "Circet already claims circet", "that colour is
        // taken" — is the thing the administrator has to act on.
        toast.error(err instanceof ApiError ? err.message : "The change could not be saved.");
        return false;
      } finally {
        setBusy(false);
      }
    },
    [load],
  );

  if (loading) return <div className="px-4 py-6 text-sm text-slate-400 lg:px-8">Loading…</div>;

  if (failed || !directory) {
    return (
      <div className="px-4 py-6 lg:px-8">
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
          <h3 className="font-semibold text-slate-700">The company list could not be loaded</h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">Reload the page, or try again shortly.</p>
        </div>
      </div>
    );
  }

  const { companies } = directory;
  const contractors = companies.filter((c) => c.kind === "CONTRACTOR");
  const internal = companies.filter((c) => c.kind === "INTERNAL");
  const people = companies.reduce((sum, c) => sum + c.users, 0);
  const attributed = contractors.reduce((sum, c) => sum + (c.rows ?? 0), 0);
  const misconfigured = contractors.filter((c) => c.unknownDmpCodes.length > 0);

  return (
    <div className="max-w-[1400px] space-y-5 px-4 py-6 lg:px-8">
      <div className="flex flex-wrap items-center gap-4">
        <div className="gradient-band flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white">
          <Building2 className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-bold text-slate-900">Companies</h2>
          <p className="text-sm text-slate-500">
            Who may sign in, which rows they see, and the colour they are drawn in.
            {directory.unattributedRows > 0 && (
              <> {directory.unattributedRows} rows in Order Activation carry no contractor at all.</>
            )}
          </p>
        </div>
        <button type="button" className="btn gradient-btn" onClick={() => setEditing("new")}>
          <PlusCircle className="h-4 w-4" /> Add Company
        </button>
      </div>

      <section className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi accent="purple" label="Contractors" value={contractors.length} note="scoped to their own rows" />
        <Kpi accent="orange" label="Internal" value={internal.length} note="unscoped, see everything" />
        <Kpi accent="rose" label="People" value={people} note="across all companies" />
        <Kpi
          accent="emerald"
          label="Rows attributed"
          value={attributed}
          note={`${directory.unattributedRows} rows carry no contractor`}
          noteTone="#c2410c"
        />
      </section>

      {/* ⚠ A code no snapshot has ever contained. Silent otherwise: the company reads as
          configured, the report is granted, and its people open it to find nothing. */}
      {misconfigured.length > 0 && (
        <div className="card flex flex-wrap items-center gap-3 p-4" style={{ borderLeft: "4px solid #f59e0b" }}>
          <AlertTriangle className="h-5 w-5 shrink-0" style={{ color: "#b45309" }} />
          <p className="min-w-[240px] flex-1 text-sm text-slate-700">
            {misconfigured.map((c) => `${c.name} claims ${c.unknownDmpCodes.join(", ")}`).join("; ")} — no order in the
            latest snapshot carries {misconfigured.length === 1 ? "that code" : "those codes"}, so{" "}
            {misconfigured.length === 1 ? "it matches" : "they match"} no rows at all.
          </p>
        </div>
      )}

      <section className="card overflow-visible">
        <div className="gradient-band flex items-center justify-between rounded-t-xl px-5 py-3 text-white">
          <span className="text-[11px] font-bold uppercase tracking-wider">Companies</span>
          <span className="text-xs text-white/85">
            {companies.length} companies · {people} people
          </span>
        </div>
        <div className="custom-scrollbar overflow-x-auto">
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 44 }} />
                <th>Company</th>
                <th>Type &amp; scope</th>
                <th>Contact</th>
                <th className="text-right">People</th>
                <th className="text-right">Rows</th>
                <th className="text-right">POPs</th>
                <th>Status</th>
                <th style={{ width: 90 }} />
              </tr>
            </thead>
            <tbody>
              {companies.map((company) => (
                <tr key={company.id} className={company.isActive ? "" : "opacity-60"}>
                  <td>
                    <span className="swatch block" style={{ background: company.colour }} />
                  </td>
                  <td>
                    <div className="font-semibold text-slate-800">{company.name}</div>
                    <div className="mono text-xs text-slate-400">
                      {company.code} · since {formatDayMonthYear(new Date(company.since))}
                    </div>
                    {company.signInDomains.length > 0 && (
                      <div className="mono text-xs text-slate-400">{company.signInDomains.join(", ")}</div>
                    )}
                  </td>
                  <td>
                    <span className={`pill pill-${company.kind === "INTERNAL" ? "orange" : "purple"}`}>
                      {company.kind === "INTERNAL" ? "Internal" : "Contractor"}
                    </span>
                    {/* ⚠ Directly under the type, because this list IS the row scope. An
                        amber code is one no order in the latest snapshot carries — fine for
                        a contractor with no work yet, and also exactly what a typo looks
                        like. */}
                    <div className="mono mt-1 text-xs text-slate-600">
                      {company.kind === "CONTRACTOR" && <span className="font-sans text-slate-400">DMP </span>}
                      {company.dmpContractorCodes.length === 0 ? (
                        <span className="text-slate-300">—</span>
                      ) : (
                        company.dmpContractorCodes.map((code) => {
                          const unknown = company.unknownDmpCodes.includes(code);
                          return (
                            <span
                              key={code}
                              className="mr-1.5 inline-block"
                              style={unknown ? { color: "#b45309" } : undefined}
                              title={unknown ? "No order in the latest snapshot carries this code" : undefined}
                            >
                              {code}
                              {unknown ? " ⚠" : ""}
                            </span>
                          );
                        })
                      )}
                    </div>
                    {company.kind === "CONTRACTOR" && (
                      <div className="mono text-xs text-slate-600">
                        <span className="font-sans text-slate-400">Monday </span>
                        {company.mondayCompanyLabels.length === 0 ? (
                          <span className="text-slate-300">—</span>
                        ) : (
                          company.mondayCompanyLabels.join(", ")
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="text-slate-700">{company.contactName ?? <span className="text-slate-300">—</span>}</div>
                    {company.contactEmail && <div className="text-xs text-slate-400">{company.contactEmail}</div>}
                  </td>
                  <td className="num text-right text-slate-700">
                    {company.users}
                    {company.users !== company.activeUsers && (
                      <span className="text-xs text-slate-400"> ({company.activeUsers} active)</span>
                    )}
                  </td>
                  {/* ⚠ An em dash for an internal company, never 0. Unifiber is not
                      attributed rows — it sees all of them — and a zero would read as
                      "Unifiber has no work". */}
                  <td className="num text-right text-slate-700">
                    {company.rows === null ? <span className="text-slate-300">—</span> : formatNumber(company.rows)}
                  </td>
                  <td className="num text-right text-slate-700">
                    {company.pops === null ? <span className="text-slate-300">—</span> : company.pops}
                  </td>
                  <td>
                    {company.isActive ? (
                      <span className="pill pill-emerald">
                        <span className="pill-dot" style={{ background: "currentColor" }} />
                        Active
                      </span>
                    ) : (
                      <span className="pill pill-slate">Inactive</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap text-right">
                    <button
                      type="button"
                      className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
                      title="Edit"
                      disabled={busy}
                      onClick={() => setEditing(company)}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    {company.isActive ? (
                      <button
                        type="button"
                        className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30"
                        title={
                          company.kind === "INTERNAL"
                            ? "Unifiber's own company cannot be deactivated"
                            : "Deactivate this company"
                        }
                        disabled={busy || company.kind === "INTERNAL"}
                        onClick={() => setConfirming(company)}
                      >
                        <Power className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline"
                        disabled={busy}
                        onClick={() =>
                          void run(
                            () => apiFetch(`/admin/companies/${company.id}/reactivate`, { method: "POST" }),
                            `${company.name} can sign in again.`,
                          )
                        }
                      >
                        Reactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {confirming && (
        <ImpactPanel
          company={confirming}
          busy={busy}
          onCancel={() => setConfirming(null)}
          onConfirm={() => {
            const name = confirming.name;
            const removable = companyImpact(confirming).removable;
            setConfirming(null);
            void run(
              () => apiFetch(`/admin/companies/${confirming.id}/deactivate`, { method: "POST" }),
              removable ? `${name} removed.` : `${name} deactivated — everyone in it is signed out.`,
            );
          }}
        />
      )}

      <p className="text-xs text-slate-400">
        A company is never deleted while it still has people or rows — deactivating it stops sign-ins and takes it out
        of the pickers, while the history stays where it is and no report total moves. Counts come from the snapshot of{" "}
        {directory.snapshot ? directory.snapshot.runDate : "— no snapshot has run yet"}.
      </p>

      {editing && (
        <CompanyDialog
          company={editing === "new" ? null : editing}
          companies={companies}
          knownDmpCodes={directory.knownDmpCodes}
          takenColours={companies.filter((c) => editing === "new" || c.id !== editing.id).map((c) => c.colour)}
          busy={busy}
          onClose={() => setEditing(null)}
          onSave={(body) => {
            const target = editing;
            const isNew = target === "new";
            void run(
              () =>
                apiFetch(isNew ? "/admin/companies" : `/admin/companies/${target.id}`, {
                  method: isNew ? "POST" : "PATCH",
                  body: JSON.stringify(body),
                }),
              isNew ? `${String(body.name)} created. It has no report access and no people yet.` : "Saved.",
            ).then((ok) => {
              if (ok) setEditing(null);
            });
          }}
        />
      )}
    </div>
  );
}

function Kpi({
  accent,
  label,
  value,
  note,
  noteTone,
}: {
  accent: "purple" | "orange" | "rose" | "emerald";
  label: string;
  value: number;
  note: string;
  noteTone?: string;
}) {
  return (
    <div className={`glass-card card-accent-${accent} rounded-xl p-5`}>
      <p className="eyebrow">{label}</p>
      <p className="num mt-1 text-3xl font-bold text-slate-900">{formatNumber(value)}</p>
      <p className="mt-1 text-xs" style={noteTone ? { color: noteTone } : { color: "#64748b" }}>
        {note}
      </p>
    </div>
  );
}

/**
 * The blast radius, stated before the button exists.
 *
 * ⚠ Every figure is real and comes from the server. The panel deliberately promises three
 * things it can keep: the people who lose access, the rows that stay, and the grants that
 * go. It does NOT say "nothing else changes" unless nothing else does.
 */
function ImpactPanel({
  company,
  busy,
  onCancel,
  onConfirm,
}: {
  company: AdminCompanyRow;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const impact = companyImpact(company);

  return (
    <section className="card card-accent-red overflow-hidden p-0">
      <div className="flex items-center gap-2 border-b border-slate-200 px-5 py-3">
        <AlertTriangle className="h-4 w-4" style={{ color: "#b91c1c" }} />
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-700">
          {impact.removable ? `Remove ${company.name}?` : `Deactivate ${company.name}?`}
        </span>
        <button type="button" className="ml-auto text-slate-400 hover:text-slate-700" onClick={onCancel} aria-label="Cancel">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="space-y-4 p-5">
        <p className="text-sm text-slate-600">
          {impact.removable
            ? `${company.name} holds no people and no rows, so it can be removed outright. Nothing else changes.`
            : `${company.name} cannot be removed — it still holds people and history. Deactivating stops sign-ins and takes it out of the pickers; every row below stays exactly where it is, and no report total moves.`}
        </p>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat label="People signed out" value={impact.users} tone={impact.users ? "#b91c1c" : undefined} />
          <Stat label="Rows kept, not deleted" value={impact.rows} />
          <Stat label="POPs in the report" value={impact.pops} />
          <Stat
            label="Report grants revoked"
            value={impact.reportGrants}
            tone={impact.reportGrants ? "#b91c1c" : undefined}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button type="button" className="btn btn-outline" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            style={{ background: "#dc2626", color: "#fff" }}
            disabled={busy}
            onClick={onConfirm}
          >
            {impact.removable ? `Remove ${company.name}` : `Deactivate ${company.name}`}
          </button>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="rounded-lg p-3" style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
      <p className="eyebrow">{label}</p>
      <p className="num mt-0.5 text-xl font-bold" style={{ color: tone ?? "#0f172a" }}>
        {formatNumber(value)}
      </p>
    </div>
  );
}

function CompanyDialog({
  company,
  companies,
  knownDmpCodes,
  takenColours,
  busy,
  onClose,
  onSave,
}: {
  company: AdminCompanyRow | null;
  companies: AdminCompanyRow[];
  knownDmpCodes: string[];
  takenColours: string[];
  busy: boolean;
  onClose: () => void;
  onSave: (body: Record<string, unknown>) => void;
}) {
  const [name, setName] = useState(company?.name ?? "");
  const [code, setCode] = useState(company?.code ?? "");
  const [kind, setKind] = useState<CompanyKind>(company?.kind ?? "CONTRACTOR");
  const [colour, setColour] = useState(
    company?.colour ?? COMPANY_PALETTE.find((c) => !takenColours.includes(c)) ?? COMPANY_PALETTE[0],
  );
  const [dmpCodes, setDmpCodes] = useState(company?.dmpContractorCodes.join(", ") ?? "");
  const [labels, setLabels] = useState(company?.mondayCompanyLabels.join(", ") ?? "");
  const [partner, setPartner] = useState<string | null>(null);
  // ⚠ The empty-scope warning is real but must not be RED before anybody has typed
  // anything — a dialog that opens complaining reads as a broken screen. Neutral until
  // they have touched a scope field, red once they have emptied it themselves.
  const [scopeTouched, setScopeTouched] = useState(false);
  const [domains, setDomains] = useState(company?.signInDomains.join(", ") ?? "");
  const [contactName, setContactName] = useState(company?.contactName ?? "");
  const [contactEmail, setContactEmail] = useState(company?.contactEmail ?? "");

  // DMP's partner list and the boards' labels, read when the dialog opens — never with
  // the directory, which must not wait on DMP or Monday.
  const [sources, setSources] = useState<CompanySources | null>(null);
  useEffect(() => {
    let live = true;
    apiFetch<CompanySources>("/admin/companies/sources")
      .then((result) => {
        if (live) setSources(result);
      })
      .catch(() => {
        if (live) {
          setSources({
            partners: { unavailable: "The partner list could not be loaded." },
            boardLabels: { unavailable: "The Monday labels could not be loaded." },
          });
        }
      });
    return () => {
      live = false;
    };
  }, []);

  const splitCodes = (value: string) =>
    value
      .split(/[,\s]+/)
      .map((v) => v.trim().toLowerCase())
      .filter(Boolean);
  // ⚠ Commas only, and the case is kept: "CIF - Circet" has spaces in it, and the Monday
  // scope matches the board's exact string (hard rule 29).
  const splitLabels = (value: string) => [
    ...new Set(
      value
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean),
    ),
  ];

  const others = companies.filter((c) => c.id !== company?.id);
  const codes = kind === "CONTRACTOR" ? splitCodes(dmpCodes) : [];
  const mondayLabels = kind === "CONTRACTOR" ? splitLabels(labels) : [];
  const codeProblem = code && !isValidCompanyCode(code) ? "Lower-case letters, digits and hyphens only." : null;
  const unknownCodes = codes.filter((c) => !knownDmpCodes.includes(c));
  const scopeProblem = companyScopeProblem({ kind, dmpContractorCodes: codes, mondayCompanyLabels: mondayLabels });

  const boardLabels = sources && "labels" in sources.boardLabels ? sources.boardLabels.labels : null;
  const unknownLabels = boardLabels ? mondayLabels.filter((l) => !boardLabels.some((b) => b.label === l)) : [];
  const labelOwner = (label: string) => others.find((c) => c.mondayCompanyLabels.includes(label)) ?? null;

  const choosePartner = (chosen: string) => {
    setPartner(chosen);
    setScopeTouched(true);
    setName(chosen);
    setCode(suggestCompanyCode(chosen));
    setLabels(mondayLabelsForPartner(chosen, boardLabels?.map((b) => b.label) ?? []).join(", "));
  };

  return (
    <Dialog title={company ? `Edit ${company.name}` : "Add Company"} onClose={onClose} wide>
      <form
        className="space-y-5 p-6"
        onSubmit={(e) => {
          e.preventDefault();
          if (codeProblem || scopeProblem) return;
          onSave({
            code,
            name,
            kind,
            colour,
            dmpContractorCodes: codes,
            mondayCompanyLabels: mondayLabels,
            signInDomains: splitCodes(domains),
            contactName: contactName.trim() || null,
            contactEmail: contactEmail.trim() || null,
          });
        }}
      >
        {/*
          Hugues, 28 Sep 2026: companies are created "based on that list of partners" —
          DMP's `homes_passed_contractor`. Picking one fills the name, the code and the
          Monday labels spelled the way the boards spell them; everything stays editable.
        */}
        {!company && kind === "CONTRACTOR" && (
          <div className="space-y-1.5">
            <label className="eyebrow">Start from a DMP partner</label>
            {!sources ? (
              <p className="text-xs text-slate-400">Reading DMP&apos;s partner list…</p>
            ) : "unavailable" in sources.partners ? (
              <p className="text-xs" style={{ color: "#b45309" }}>
                {sources.partners.unavailable} The fields below still accept typed values.
              </p>
            ) : (
              <div className="flex flex-wrap items-center gap-1.5">
                {sources.partners.names.map((candidate) => {
                  const existing = companyForPartner(candidate, companies);
                  return (
                    <button
                      key={candidate}
                      type="button"
                      className={`chip${partner === candidate ? " chip-active" : ""}`}
                      style={existing ? { opacity: 0.45, cursor: "not-allowed" } : undefined}
                      disabled={existing !== null}
                      title={existing ? `Already a company: ${existing.name}` : undefined}
                      onClick={() => choosePartner(candidate)}
                    >
                      {candidate}
                      {existing && <span className="font-normal">· {existing.name}</span>}
                    </button>
                  );
                })}
              </div>
            )}
            <p className="text-xs text-slate-400">
              The homes-passed partners DMP knows (<span className="mono">homes_passed_contractor</span>).
            </p>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label className="eyebrow">Company name</label>
            <input
              type="text"
              className="field"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nexus Telecom"
              required
            />
          </div>
          <div className="space-y-1.5">
            <label className="eyebrow">Code</label>
            <input
              type="text"
              className="field mono"
              value={code}
              onChange={(e) => setCode(e.target.value.toLowerCase())}
              placeholder="nexus"
              required
            />
            <p className="text-xs" style={codeProblem ? { color: "#b91c1c" } : { color: "#94a3b8" }}>
              {codeProblem ?? "Lower-case, no spaces. Used in exports and links."}
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <label className="eyebrow">Type</label>
          <div className="grid gap-3 sm:grid-cols-2">
            <PickCard
              selected={kind === "CONTRACTOR"}
              onSelect={() => setKind("CONTRACTOR")}
              title="Contractor"
              description="Sees only its own rows, in the drawings opened to it."
              disabled={company !== null && company.kind !== "CONTRACTOR"}
              disabledReason="Changing the type would change what everybody in it can see"
            />
            <PickCard
              selected={kind === "INTERNAL"}
              onSelect={() => setKind("INTERNAL")}
              title="Internal"
              description="Unifiber staff. Unscoped, sees every row."
              disabled={company !== null && company.kind !== "INTERNAL"}
              disabledReason="Changing the type would change what everybody in it can see"
            />
          </div>
        </div>

        {/*
          ⚠ Two scopes, from two different lists in DMP (see `companyScopeProblem`). The
          Monday labels decide what a company sees on Cocon Requests and Uploads; the DMP
          codes decide what it sees on Order Activation. A homes-passed partner has only
          the first — DMP never writes its name on an order — and that must be allowed.
        */}
        {kind === "CONTRACTOR" && (
          <div className="space-y-1.5">
            <label className="eyebrow">Monday company labels</label>
            <input
              type="text"
              className="field mono"
              value={labels}
              onChange={(e) => {
                setScopeTouched(true);
                setLabels(e.target.value);
              }}
              placeholder="Walfit"
            />
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-slate-400">On the boards:</span>
              {!sources && <span className="text-xs text-slate-400">reading Monday…</span>}
              {sources && "unavailable" in sources.boardLabels && (
                <span className="text-xs" style={{ color: "#b45309" }}>
                  {sources.boardLabels.unavailable}
                </span>
              )}
              {boardLabels?.map((entry) => {
                const owner = labelOwner(entry.label);
                const active = mondayLabels.includes(entry.label);
                return (
                  <button
                    key={entry.label}
                    type="button"
                    className={`chip${active ? " chip-active" : ""}`}
                    style={owner && !active ? { opacity: 0.45, cursor: "not-allowed" } : undefined}
                    disabled={owner !== null && !active}
                    title={
                      owner
                        ? `Claimed by ${owner.name}`
                        : `${entry.requests} on Cocon requests · ${entry.uploads} on Cocon Uploads Tracking`
                    }
                    onClick={() => {
                      setScopeTouched(true);
                      setLabels(
                        (active ? mondayLabels.filter((l) => l !== entry.label) : [...mondayLabels, entry.label]).join(
                          ", ",
                        ),
                      );
                    }}
                  >
                    {entry.label}
                  </button>
                );
              })}
            </div>
            {unknownLabels.length > 0 ? (
              <p className="text-xs" style={{ color: "#b45309" }}>
                ⚠ No item on either board carries {unknownLabels.map((l) => `"${l}"`).join(", ")}. That is fine for a
                partner with no tickets yet, and it is also what a typo looks like — the match is exact, capitals and
                spaces included.
              </p>
            ) : (
              <p className="text-xs text-slate-400">
                The Company values on the Monday boards that belong to this company, spelled exactly as there. It is
                what decides which tickets they see on Cocon Requests and Cocon Uploads Tracking.
              </p>
            )}
          </div>
        )}

        {/*
          ⚠ Not in the mockup, and it has to be. This list IS Order Activation's row scope —
          `contractorScope` returns it, `matchesScope` filters on it — so a mistyped code
          matches no rows, silently.
        */}
        {kind === "CONTRACTOR" && (
          <div className="space-y-1.5">
            <label className="eyebrow">DMP contractor codes · Order Activation</label>
            <input
              type="text"
              className="field mono"
              value={dmpCodes}
              onChange={(e) => {
                setScopeTouched(true);
                setDmpCodes(e.target.value);
              }}
              placeholder="leave empty for a homes-passed partner"
            />
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-slate-400">In the latest snapshot:</span>
              {knownDmpCodes.map((known) => {
                const owner = others.find((c) => c.dmpContractorCodes.includes(known)) ?? null;
                const active = codes.includes(known);
                return (
                <button
                  key={known}
                  type="button"
                  className={`chip${active ? " chip-active" : ""}`}
                  style={owner && !active ? { opacity: 0.45, cursor: "not-allowed" } : undefined}
                  disabled={owner !== null && !active}
                  title={owner ? `Claimed by ${owner.name}` : undefined}
                  onClick={() => {
                    setScopeTouched(true);
                    setDmpCodes(
                      codes.includes(known)
                        ? codes.filter((c) => c !== known).join(", ")
                        : [...codes, known].join(", "),
                    );
                  }}
                >
                  {known}
                </button>
                );
              })}
              {knownDmpCodes.length === 0 && <span className="text-xs text-slate-400">no snapshot has run yet</span>}
            </div>
            {unknownCodes.length > 0 ? (
              <p className="text-xs" style={{ color: "#b45309" }}>
                ⚠ No order in the latest snapshot carries {unknownCodes.join(", ")}. DMP only ever writes the
                homes-connect contractor on an order — a homes-passed partner&apos;s name never appears there, so this
                company would see no Order Activation rows at all.
              </p>
            ) : (
              <p className="text-xs text-slate-400">
                The homes-connect contractor DMP writes on an order. Leave it empty for a
                homes-passed partner: they have no rows in Order Activation.
              </p>
            )}
          </div>
        )}

        {scopeProblem && (
          <p className="text-xs" style={{ color: scopeTouched ? "#b91c1c" : "#94a3b8" }}>
            {scopeProblem}
          </p>
        )}

        <div className="space-y-1.5">
          <label className="eyebrow">Sign-in domains</label>
          <input
            type="text"
            className="field mono"
            value={domains}
            onChange={(e) => setDomains(e.target.value)}
            placeholder="nexus.be, nexus-telecom.fr"
          />
          <p className="text-xs text-slate-400">
            {/* ⚠ Informational only, and the schema says so too. Ubi was explicit that
                subcontractor staff use personal addresses, so membership is always the
                explicit company on the person and never inferred from their domain. */}
            A note for whoever invites people — nothing is enforced on it, because half of these staff sign in with a
            personal address.
          </p>
        </div>

        <div className="space-y-2">
          <label className="eyebrow">Chart colour</label>
          <div className="flex flex-wrap gap-2">
            {COMPANY_PALETTE.map((option) => {
              const taken = takenColours.includes(option);
              return (
                <button
                  key={option}
                  type="button"
                  className={`colour-pick${colour === option ? " sel" : ""}`}
                  style={{ background: option }}
                  disabled={taken}
                  title={taken ? "Already used by another company" : option}
                  aria-label={option}
                  onClick={() => setColour(option)}
                />
              );
            })}
          </div>
          <p className="text-xs text-slate-400">
            Used on every chart in every report. Two companies cannot share one — it is the only thing telling them
            apart on a stacked bar.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label className="eyebrow">Main contact</label>
            <input
              type="text"
              className="field"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              placeholder="Jan Peeters"
            />
          </div>
          <div className="space-y-1.5">
            <label className="eyebrow">Contact email</label>
            <input
              type="email"
              className="field"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="j.peeters@nexus.be"
            />
          </div>
        </div>

        {!company && (
          <div className="flex items-start gap-2.5 rounded-lg px-3.5 py-2.5" style={{ background: "#eff6ff", border: "1px solid #bedbfe" }}>
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" style={{ color: "#1d4fd7" }} />
            <p className="text-xs" style={{ color: "#1d4fd7" }}>
              A new company starts with <span className="font-semibold">no report access and no people</span>. Grant it
              a report on <span className="font-semibold">Report Access</span>, then invite its staff on{" "}
              <span className="font-semibold">Users</span>.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="btn btn-outline">
            Cancel
          </button>
          <button type="submit" className="btn gradient-btn" disabled={busy || codeProblem !== null || scopeProblem !== null}>
            {company ? "Save Changes" : "Create Company"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
